import React from "react";
import { create } from "zustand";
import { getTool, getToolList, updateTool, createTool, deleteTool } from "./toolApi";
import { notifications } from "@mantine/notifications"; // For later use, if deciding to trigger from store
import { produce } from "immer"; // For easier state updates

export const dictToList = (dict) =>
  Object.entries(dict || {}).map(([env, url]) => ({ env, url }));

export const listToDict = (list) =>
  Object.fromEntries((list || []).map(({ env, url }) => [env, url]));

export const headersDictToList = (dict) =>
  Object.entries(dict || {}).map(([key, value]) => ({ key, value }));

export const headersListToDict = (list) =>
  Object.fromEntries((list || []).map(({ key, value }) => [key, value]));

// Environment overrides (spec K-8): { env: { webUrl, headers, mcpServerId } } dict <-> form
// list. Merges the legacy per-env webUrls dict into the list on load, so existing entries
// show up (and migrate into environmentOverrides on the next save).
export const overridesDictToList = (overridesDict, legacyWebUrls) => {
  const map = new Map();
  Object.entries(legacyWebUrls || {}).forEach(([env, url]) => {
    map.set(env, { env, webUrl: url || "", headers: [] });
  });
  Object.entries(overridesDict || {}).forEach(([env, o]) => {
    const existing = map.get(env) || { env, webUrl: "", headers: [] };
    map.set(env, {
      env,
      webUrl: o?.webUrl || existing.webUrl || "",
      headers: headersDictToList(o?.headers),
    });
  });
  return [...map.values()];
};

export const overridesListToDict = (list) => {
  const entries = (list || [])
    .filter((o) => o.env)
    .map((o) => {
      const headers = headersListToDict((o.headers || []).filter((h) => h.key));
      return [
        o.env,
        {
          webUrl: o.webUrl || null,
          headers: Object.keys(headers).length > 0 ? headers : null,
        },
      ];
    });
  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

// alterantively, use create(immer((set, get) => ({ ... } );
// and then use mutators like set((state) => { state.tools = ... } )
const useToolStore = create((set, get) => ({
  tools: [],
  toolsPaged: {},
  tool: null,
  // originalTool: null,
  isLoading: false,
  isSaving: false,
  error: null,
  hasFetched: false,
  // hasUnsavedChanges: false,
  clearError: () => {
    set({ error: null });
  },
  fetchTools: async () => {
    set({ isLoading: true, error: null });
    var toolList = await getToolList();
    set({
      isLoading: false,
      toolsPaged: toolList,
      tools: toolList.data,
      error: null,
      hasFetched: true,
    });
  },
  fetchTool: async (toolId) => {
    // Clear the current tool immediately to prevent showing stale data
    const currentTool = get().tool;
    if (currentTool?.id !== toolId) {
      set({ tool: null, isLoading: true, error: null });
    } else {
      set({ isLoading: true, error: null });
    }
    try {
      const fetchedTool = await getTool(toolId);
      if (fetchedTool) {
        set({
          tool: fetchedTool,
          isLoading: false,
          // hasUnsavedChanges: false,
        });
      } else {
        set({ error: "Tool not found", isLoading: false });
      }
    } catch (err) {
      console.error("Failed to fetch tool", err);
      set({ error: "Failed to fetch tool", isLoading: false });
      notifications.show({
        title: "Failed to fetch tool",
        message: "An error occurred while fetching the tool.",
        color: "red",
      });
    }
  },

  setTool: (newTool) => {
    set(
      produce((draft) => {
        draft.tool = newTool;
      })
    );
  },

  createTool: async (toolData) => {
    set({ isSaving: true, error: null });
    try {
      const newTool = await createTool(toolData);
      set((state) => ({
        tools: [...state.tools, newTool],
        toolsPaged: {
          ...state.toolsPaged,
          data: [...(state.toolsPaged?.data ?? []), newTool],
        },
        tool: newTool,
        isSaving: false,
      }));
      return { success: true, tool: newTool };
    } catch (err) {
      console.error("Failed to create tool:", err);
      set({ error: "Failed to create tool", isSaving: false });
      throw err;
    }
  },

  deleteTool: async (toolId) => {
    try {
      await deleteTool(toolId);
      set(
        produce((draft) => {
          draft.tools = draft.tools.filter((t) => t.id !== toolId);
          if (draft.toolsPaged.data) {
            draft.toolsPaged.data = draft.toolsPaged.data.filter(
              (t) => t.id !== toolId,
            );
          }
          if (draft.tool?.id === toolId) {
            draft.tool = null;
          }
        }),
      );
    } catch (err) {
      console.error("Failed to delete tool:", err);
      notifications.show({
        title: "Failed to delete tool",
        message: "An error occurred while deleting the tool.",
        color: "red",
      });
      throw err;
    }
  },

  updateTool: async (toolId, updatedData) => {
    set({ isSaving: true, error: null });
    try {
      const updatedTool = await updateTool(toolId, updatedData);

      set(
        produce((draft) => {
          draft.tool = updatedTool;
          const index = draft.tools.findIndex(
            (item) => item.id === updatedTool.id
          );
          if (index !== -1) {
            draft.tools[index] = updatedTool;
          }
          const indexPaged = draft.toolsPaged.data?.findIndex(
            (item) => item.id === updatedTool.id
          );
          if (indexPaged && indexPaged !== -1) {
            draft.toolsPaged.data[indexPaged] = updatedTool;
          }
          draft.isSaving = false;
        })
      );
      return { success: true, tool: updatedTool };
    } catch (err) {
      console.error("Failed to update tool:", err);
      set({ error: "Failed to update tool", isSaving: false });
      throw err;
    }
  },
}));

// Custom hook that auto-fetches tools if not already loaded
export const useTools = () => {
  const tools = useToolStore((state) => state.tools);
  const isLoading = useToolStore((state) => state.isLoading);
  const hasFetched = useToolStore((state) => state.hasFetched);
  const fetchTools = useToolStore((state) => state.fetchTools);

  // Auto-fetch on mount if not already fetched
  React.useEffect(() => {
    if (!hasFetched && !isLoading) {
      fetchTools();
    }
  }, [hasFetched, isLoading, fetchTools]);

  return { tools, isLoading };
};

export default useToolStore;
